import React, { useEffect } from 'react';
import Select from 'react-select';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import { statusOptions } from '../../constants/appConstants';
import { useSearchParams } from 'react-router-dom';

const DashboardFilters = ({
  organizationList,
  centerList,
  organizationFilter,
  setOrganizationFilter,
  centerFilter,
  setCenterFilter,
  selectedStatusesFilter,
  setSelectedStatusesFilter,
  generalSearchFilter,
  setGeneralSearchFilter
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Sync URL parameters with filter state on component mount
  useEffect(() => {
    const orgFromUrl = searchParams.get('organization');
    const centerFromUrl = searchParams.get('center');
    const searchFromUrl = searchParams.get('search');
    
    // Only update state if the URL parameters exist and differ from current state
    if (orgFromUrl && orgFromUrl !== organizationFilter) {
      setOrganizationFilter(orgFromUrl);
    }
    
    if (centerFromUrl && centerFromUrl !== centerFilter) {
      setCenterFilter(centerFromUrl);
    }
    
    if (searchFromUrl && searchFromUrl !== generalSearchFilter) {
      setGeneralSearchFilter(searchFromUrl);
    }
  }, [searchParams, organizationFilter, centerFilter, generalSearchFilter, setOrganizationFilter, setCenterFilter, setGeneralSearchFilter]);
    
  // Update URL when filters change
  const updateUrlParams = (key, value) => {
    // Create a new URLSearchParams object from the current search params
    const newSearchParams = new URLSearchParams(searchParams);
    
    // If value is empty, remove the parameter, otherwise set it
    if (value === '' || value === null) {
      newSearchParams.delete(key);
    } else {
      newSearchParams.set(key, value);
    }
    
    // Update URL without reloading the page
    setSearchParams(newSearchParams, { replace: true });
  };
  
  // Handle organization change
  const handleOrganizationChange = (e) => {
    const value = e.target.value;
    setOrganizationFilter(value);
    updateUrlParams('organization', value);
  };
  
  // Handle center change
  const handleCenterChange = (e) => {
    const value = e.target.value;
    setCenterFilter(value);
    updateUrlParams('center', value);
  };
  
  // Handle status filter change
  const handleStatusChange = (selected) => {
    setSelectedStatusesFilter(selected);
    // For multi-select, you might want to handle this differently
    // This example just checks if there are any selections
    if (selected && selected.length > 0) {
      updateUrlParams('status', selected.map(s => s.value).join(','));
    } else {
      updateUrlParams('status', null);
    }
  };
  
  // Handle search filter change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setGeneralSearchFilter(value);
    // Debounce this in a real application
    updateUrlParams('search', value || null);
  };

  return (
    <div className="row g-3 pb-4">
      <div className="col-3 me-2 mobile-hidden">
        <select
          id="dashboard-organization-filter"
          name="organization-filter"
          className='form-select'
          value={organizationFilter}
          onChange={handleOrganizationChange}
        >
          <option value="">All Organizations</option>
          {organizationList.map((organization, index) => (
            <option key={index} value={organization}>{organization}</option>
          ))}
        </select>
      </div>
      <div className="col-3 ms-0 p-0 me-2 mobile-hidden">
        <select
          id="dashboard-center-filter"
          name="center-filter"
          className='form-select'
          value={centerFilter}
          onChange={handleCenterChange}
        >
          <option value="">All Centers</option>
          {centerList.map((center, index) => (
            <option key={index} value={center}>{center}</option>
          ))}
        </select>
      </div>
      <div className="col-3 ms-0 mobile-hidden">
        <Select
          inputId="dashboard-status-filter-select"
          instanceId="dashboard-status-filter-instance" 
          name="status-filter"
          value={selectedStatusesFilter}
          isMulti
          options={statusOptions}
          onChange={handleStatusChange}
          className="basic-multi-select dashboard-multiselect clear-background"
          classNamePrefix="select"
          placeholder="Filter by State"
          styles={{
            placeholder: (provided) => ({
              ...provided,
              textAlign: 'left',
              color: 'black',
            }),
            valueContainer: (provided) => ({
              ...provided,
              justifyContent: 'flex-start',
            }),
          }}
        />
      </div>
      <div className="col-2 m-0 mt-3 ms-auto form-search mobile-full-width">
        <div className="input-container ms-0">
          <span className="icon"><FontAwesomeIcon icon={faMagnifyingGlass} style={{ color: '#212529' }}/></span>
          <input
            id="dashboard-general-search"
            name="general-search"
            className='form-control dark-placeholder-color'
            type="text"
            placeholder="Search units, organizations, centers..."
            style={{ borderRadius: '21px' }}
            value={generalSearchFilter}
            onChange={handleSearchChange}
            autoComplete="off"
          />
        </div>
      </div>
    </div>
  );
};

export default DashboardFilters;